import { ReduxControls } from 'rhombus-react';

import { tellSentry } from "utils/Rave";
import { ASSET_TYPES } from 'utils/constants';
import {
	noop,
	isAfterEffectsCreative,
	isImposiumCreative,
	isSocialCreative
} from "utils/common";


/*
	Variables have a `prop` that maps to the Asset's `url` property. In order to update
	image variables on approved creatives we need to pass the `prop` to the Version Variable.
	The `prop` is also used to store the original value of text asset version variables, to
	allow for text asset formatting.
*/
export function mapAssetToVersionVariable(variableName, asset, prop=""){
	const defaultVersionVariables = {
		name:     variableName,
		data:     asset.name,
		type:     asset.assetType,
		assetId:  asset.id,
		filesize: asset.filesize,
	};

	if(asset.assetType === ASSET_TYPES.TEXT){
		return {
			...defaultVersionVariables,
			prop: asset.name,
			isFormatted: false,
			label: `${asset.name} (${asset.id})`,
		};
	}

	if(!!prop) {
		return { ...defaultVersionVariables, prop };
	}

	return defaultVersionVariables;
}


export function getModalProps({ assetType, onClose }){
	return  {
		title:           `Select ${assetType}`,
		type:            "modal",
		className:       "edit-version",
		xClose:          true,
		allowFullScreen: false,
		bladeProps:      { verticallyCenter: true },
		setAppTitle:     false,
		onClose,
	};
}

export function buildAdvancedFilter({ type, accept, videoEncodingFilter } = {}){
	/*
		This builds a JCRM `advancedFilter` for Asset searches, to restrict selectable options

		`type` is the Asset type (e.g. "Image", "Video", "Audio")

		`accept` is expected to be a string with a comma separated list of acceptable file extensions, with no spaces.
		e.g. ".mp4,.mov"
			- Non-specific types (e.g. "video/*"), which are appropriate for file inputs, are not appropriate
			here.

		`videoEncodingFilter` is expected to be a string, either "true" or "false"
	*/
	if(!type){
		console.error("`type` is required to build an asset filter");
		return;
	}
	const terms = [`eq(assetType,${type})`];

	if(accept){
		const extensions = accept.split(",");
		const isAllExtensions = extensions.every(extension => /^\.[^\.]+$/.test(extension));
		if(isAllExtensions){
			const acceptTerms = extensions.map(ext => `like(name,*${ext})`).join(",");
			terms.push(`any(${acceptTerms})`);
		} else {
			console.error("Invalid accept value for API requests", accept);
		}
	}

	if(type === ASSET_TYPES.VIDEO && videoEncodingFilter){
		terms.push(`eq(encoded,${videoEncodingFilter})`);
	}

	if(terms.length > 1){
		return `all(${terms.join(",")})`;
	}

	return terms[0];
}


export function getSelectProps({
	extraSelectProps = {},
	name = "",
	search = noop,
	type,
	videoEncodingFilter = "",
	accept,
}){
	let isText = type === ASSET_TYPES.TEXT
		, defaultSearchParams = {
			rpp:        50,
			sort:       "name:asc",
			searchTerm: "",
			advancedFilter: buildAdvancedFilter({ type, accept, videoEncodingFilter }),
		}
		;

	return {
		isAsync:             true,
		isSearchable:        true,
		icon:                "search",
		showIcon:            true,
		label:               name,
		placeholder:         "Search and Select...",
		autoFocus:           true,
		onSelectResetsInput: false,
		onMenuScrollToBottom: noop,
		defaultOptions:      true,
		loadOptions(val, callback){
			// Returning a value will short circuit the callback flow, so we just call `search`, but don't return its value.
			if(!val){
				search(defaultSearchParams, callback);
			} else {
				search({
					...defaultSearchParams,
					rpp: -1,
					searchTerm: val,
				}, callback);
			}
		},
		normalize(val, previousValue, allValues, previousAllValues, name){
			/*
				The Assets endpoint data and the Version variable data are a little bit mismatched, so we have to normalize to the format that the blade and the API expect.
				Notably, this doesn't update the `prop` value, which is an Asset URL for these variables.
				This shouldn't affect the POST working, so long as the assetId and type are set properly.
			*/
			if(!val){ return previousValue; }

			if(isText){
				if(previousValue.assetId === val.assetId){
					return previousValue;
				}
				return {
					...previousValue,
					...val,
					prop:        val.data,
					isFormatted: false,
				};
			}

			let valName = new RegExp(val.data)
				, propURL = previousValue.prop
				;

			/**
			 * HOPI-1735
			 * 	The ?dirty=true flag does not actually update the `prop` value of version variables
			 * 	When using the drop down, the UI doesn't have the `prop` value
			 * 		This meant when making a preview, the API returned the wrong URL field for the render
			 * 	We shouldn't construct the URL manually because it might not exist, or be correct.
			 * 		It also limits us in case JCRM puts assets into new places
			 * 	We need to replace the prior value with the new value
			 * 		The prior value *might* not be the same value as the current saved value if the user changes the field several times
			 *
			 * 	Temporary workaround until PRVW supports versions
			 * 	Ideally, the API would do a lookup on the `assetId` field and construct the correct value in the response
			 */
			if(!valName.test(previousValue.prop)){
				try {
					propURL = previousValue.prop.replace(previousValue.data, val.data);
				} catch(e){
					tellSentry(`Unable to replace asset URL - ${previousValue.prop} with ${val.data}`);
				}
			}

			return {
				...previousValue,
				data: val.data,
				prop:propURL,
				assetId: val.assetId,
				filesize: val.filesize,
			};
		},

		...extraSelectProps,

		component: ReduxControls.Select,
	};
}

export function shouldUseUnencodedVideos(creative={}) {
	let isAdStudioCreative = /adstudio/gi.test(creative.origin);
	let isDynamicPrerollCreative = /dynamic_preroll/gi.test(creative.adType);
	const isAfterEffects = isAfterEffectsCreative(creative);
	const isSocial = isSocialCreative(creative);
	const isImposium = isImposiumCreative(creative);

	return [
		isAdStudioCreative && isDynamicPrerollCreative,
		isAfterEffects,
		isSocial,
		isImposium
	].some(cond => cond);
}

export function sanitizeVariableFieldName(name){
	/*
		We generally use version variable names as the name of the variable's field in version edit forms.
		It's valid for some versions to have variable names with periods in them, which is a problem
		in redux-form, because it will interpret it as a nested value.

		Currently, version names are restricted to just alphanumerics, underscores and periods,
		so we can just replace periods with another character that's not allowed to be in a version name
		to have an unambiguous identifier.

		The general approach is to use a sanitized name for the field name (and associated `change` actions, etc.),
		and leave the value's `name` property untouched, so it can be used as is for display purposes,
		and to identify the variable correctly when we send it back to the API.
	*/
	return name.replaceAll(".", "*");
}
